/*
 * kw-lightbox.js (Kleine Wolke, eigene Datei)
 * Stand: 21.10.2026, Fassung 4
 * Fassung 2: Lage des Labels auf ganze Bildschirmpixel gerundet. Bei
 * Windows-Skalierung (z. B. 150 %) lag das Label sonst auf halben
 * Bildschirmpixeln, die Firefox beim Zoomen mal ab-, mal aufrundet; das
 * Label sprang dadurch um einen Pixel nach links und rechts.
 * Fassung 3: eigener Platzhalter je Slide (Punkt 3 unten).
 * Fassung 4: Bedienelemente auf Touch-Geraeten dauerhaft sichtbar (Punkt 4 unten).
 *
 * Ergaenzt die Produktbild-Lightbox des Themes (PhotoSwipe 5.4.4 aus
 * assets/media-gallery.js), ohne media-gallery.js zu aendern:
 *
 * 1. KI-Label: Hat ein Produktbild das EU-KI-Label (.kw-ai-label aus
 *    snippets/product-thumbnail.liquid), erscheint das Label auch in der
 *    grossen Ansicht: unten mittig auf dem Bild, 100 px breit und 15 px
 *    ueber der Unterkante wie auf dem Thumbnail, mit demselben Bild und
 *    demselben Alt-Text. Beim Hineinzoomen bleibt es gleich gross und
 *    bleibt im sichtbaren Teil des Bildes ueber der Bedienleiste.
 * 2. Bedienleiste: Zurueck, Schliessen und Weiter liegen in einer Leiste
 *    unten mittig (.pswp__top-bar in assets/photoswipe-component.css).
 *    Ueber die PhotoSwipe-Option paddingFn bekommt die Bildflaeche unten
 *    einen Abstand, damit das Bild in der Normalansicht ueber der Leiste
 *    endet. Gilt fuer alle Bildschirmgroessen.
 * 3. Platzhalter: Solange das grosse Bild einer Slide laedt, zeigt
 *    PhotoSwipe einen Platzhalter. Der Filter placeholderSrc des Themes
 *    gibt dafuer jeder Slide das gerade aktive Bild des Seiten-Sliders
 *    (meist das erste Bild); beim schnellen Blaettern erschien es auf die
 *    Breite der Slide gestreckt und stark vergroessert. Ein eigener Filter
 *    (laeuft nach dem des Themes) gibt jeder Slide ihr eigenes, schon
 *    geladenes Bild: zuerst das Bild im Seiten-Slider, sonst das kleine Bild
 *    aus der Thumbnail-Leiste. Ist keines geladen, gibt es keinen
 *    Bild-Platzhalter; die dann gezeigte Flaeche ist transparent statt
 *    dunkel (#222 aus photoswipe-component.css).
 * 4. Bedienelemente auf Touch-Geraeten: PhotoSwipe blendet sie ab Werk bei
 *    einem einfachen Tipp aus und wieder ein (Option tapAction
 *    "toggle-controls"). tapAction steht hier auf false: ein einfacher Tipp
 *    macht nichts, die Bedienelemente bleiben sichtbar. Doppeltipp zoomt
 *    weiter, Schliessen ueber das X oder durch Wischen nach unten.
 *    Mausklicks am Desktop laufen ueber eigene Optionen und bleiben gleich.
 *
 * Einbindung: snippets/product-media-gallery.liquid (Script-Zeile nach
 * </media-gallery>). Die Datei klinkt sich in die Methode initImageZoom
 * des Elements media-gallery ein. Benennt ein Theme-Update diese Methode
 * oder die Eigenschaft lightbox um, faellt nur diese Ergaenzung weg, die
 * Lightbox des Themes laeuft unveraendert weiter.
 */
(() => {
  // Das Snippet kann mehrfach auf einer Seite stehen: nur einmal ausfuehren
  if (window.kwLightboxLoaded) return;
  window.kwLightboxLoaded = true;

  /** Breite des Labels in px, wie auf dem Thumbnail */
  const LABEL_WIDTH = 100;
  /** Abstand des Labels zur Unterkante des Bildes in px, wie auf dem Thumbnail */
  const LABEL_OFFSET = 15;
  /** Luft zwischen Bildunterkante und Oberkante der Knoepfe in px */
  const BAR_GAP = 15;
  /** Klasse am PhotoSwipe-Root, solange die Lightbox offen ist (nach der Oeffnen-Animation) */
  const READY_CLASS = 'kw-pswp--ready';
  const STYLE_ID = 'kw-lightbox-styles';

  /**
   * Abstand unten fuer die Bildflaeche in px.
   * Laut photoswipe-component.css: Leiste 3rem ueber dem Rand, Knopf "Schliessen"
   * 6rem hoch mit 15 px Aussenabstand. Oberkante des Knopfs = 9rem + 15 px,
   * dazu BAR_GAP Luft. Bei 1rem = 10 px (html font-size 62.5 %) sind das 120 px.
   * @returns {number}
   */
  const getBottomPadding = () => {
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
    return Math.round(9 * rem + 15 + BAR_GAP);
  };

  let bottomPadding = getBottomPadding();

  /**
   * Fuehrt eine Funktion aus, ohne dass ein Fehler die Lightbox des Themes stoert.
   * @param {Function} fn
   * @returns {Function}
   */
  const safe = (fn) => (...args) => {
    try {
      return fn(...args);
    } catch (error) {
      console.error('kw-lightbox:', error);
      return undefined;
    }
  };

  /** Styles einmal in den Head schreiben */
  const injectStyles = () => {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
.kw-pswp-ai-label {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  width: ${LABEL_WIDTH}px;
  line-height: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}
.kw-pswp-ai-label__image {
  display: block;
  width: 100%;
  height: auto;
}
.pswp.${READY_CLASS} .kw-pswp-ai-label {
  opacity: 1;
}
/* Platzhalter ohne Bild: transparent statt #222 (photoswipe-component.css, Selektor dort 0-1-1) */
.pswp div.pswp__img--placeholder {
  background: transparent;
}
@media (prefers-reduced-motion: reduce) {
  .kw-pswp-ai-label {
    transition: none;
  }
}`;
    document.head.appendChild(style);
  };

  /**
   * Sucht das KI-Label des Produktbildes zur Slide und baut daraus das Label fuer die Lightbox.
   * Die Slide-ID ist data-media-index des Produktbildes (so baut media-gallery.js die Liste).
   * @param {HTMLElement} gallery Element media-gallery
   * @param {object} slide PhotoSwipe-Slide
   * @returns {HTMLElement | null}
   */
  const createLabel = (gallery, slide) => {
    const data = slide.data;
    if (!data || data.mediaType !== 'image') return null;
    if (data.id === undefined || data.id === null) return null;

    const mediaIndex = CSS.escape(String(data.id));
    const sourceImage = gallery.querySelector(
      `.product__media-item[data-media-index="${mediaIndex}"]:not(.swiper-slide-duplicate) .kw-ai-label img`
    );
    if (!sourceImage) return null;

    // Kopie des Label-Bildes: gleiche Datei, gleicher Alt-Text, eigene Gestaltung
    const image = sourceImage.cloneNode(false);
    image.removeAttribute('style');
    image.removeAttribute('loading');
    image.setAttribute('sizes', `${LABEL_WIDTH}px`);
    image.classList.add('kw-pswp-ai-label__image');

    const label = document.createElement('div');
    label.className = 'kw-pswp-ai-label';
    label.appendChild(image);
    return label;
  };

  /**
   * Liefert die Adresse eines schon geladenen Bildes derselben Slide als Platzhalter:
   * zuerst das Bild im Seiten-Slider, sonst das kleine Bild aus der Thumbnail-Leiste.
   * Ist keines geladen: false (PhotoSwipe zeigt dann eine leere Flaeche).
   * @param {HTMLElement} gallery Element media-gallery
   * @param {object} content PhotoSwipe-Content der Slide
   * @returns {string | false}
   */
  const findPlaceholderSrc = (gallery, content) => {
    const data = content && content.data;
    if (!data || data.mediaType !== 'image') return false;
    if (data.id === undefined || data.id === null) return false;

    const mediaIndex = CSS.escape(String(data.id));
    const mediaItem = gallery.querySelector(
      `.product__media-item[data-media-index="${mediaIndex}"]:not(.swiper-slide-duplicate)`
    );
    if (!mediaItem) return false;

    const candidates = [mediaItem.querySelector('.product__media img')];
    const mediaId = mediaItem.dataset.mediaId;
    if (mediaId) {
      candidates.push(gallery.querySelector(`.product__thumbs-item[data-target="${CSS.escape(mediaId)}"] img`));
    }

    for (const image of candidates) {
      if (!image || !image.complete || image.naturalWidth === 0) continue;
      const src = image.currentSrc || image.src;
      if (src) return src;
    }
    return false;
  };

  /**
   * Rundet einen Wert in CSS-Pixeln auf ganze Bildschirmpixel.
   * Bei Skalierung 125 % oder 150 % liegen ganze CSS-Pixel sonst oft auf halben
   * Bildschirmpixeln (z. B. 719 px x 1,5 = 1078,5).
   * @param {number} value Wert in CSS-Pixeln
   * @returns {number}
   */
  const snapToDevicePixel = (value) => {
    const ratio = window.devicePixelRatio || 1;
    return Math.round(value * ratio) / ratio;
  };

  /**
   * Hoehe des Labels in CSS-Pixeln, ohne das Layout abzufragen:
   * aus dem Seitenverhaeltnis des geladenen Bildes, vorher aus width/height des img.
   * @param {HTMLElement} label
   * @returns {number}
   */
  const getLabelHeight = (label) => {
    const image = label.firstElementChild;
    if (!image) return 0;
    if (image.naturalWidth > 0 && image.naturalHeight > 0) {
      return (LABEL_WIDTH * image.naturalHeight) / image.naturalWidth;
    }
    const width = Number(image.getAttribute('width'));
    const height = Number(image.getAttribute('height'));
    if (width > 0 && height > 0) return (LABEL_WIDTH * height) / width;
    return 0;
  };

  /**
   * Setzt das Label unten mittig auf den sichtbaren Teil des Bildes.
   * Bildlage aus PhotoSwipe: pan = linke obere Ecke im Slide, Groesse = Bildmass x Zoomstufe.
   * Gesetzt wird die linke obere Ecke des Labels, gerundet auf ganze Bildschirmpixel.
   * @param {object} slide PhotoSwipe-Slide
   */
  const positionLabel = (slide) => {
    const label = slide.kwAiLabel;
    if (!label || !slide.pswp) return;

    const viewport = slide.pswp.viewportSize;
    const zoom = slide.currZoomLevel;
    const imageLeft = slide.pan.x;
    const imageTop = slide.pan.y;
    const imageRight = imageLeft + slide.width * zoom;
    const imageBottom = imageTop + slide.height * zoom;

    const visibleLeft = Math.max(imageLeft, 0);
    const visibleRight = Math.min(imageRight, viewport.x);
    const visibleBottom = Math.min(imageBottom, viewport.y - bottomPadding);

    const centerX = (visibleLeft + visibleRight) / 2;
    const labelBottom = visibleBottom - LABEL_OFFSET;

    const left = snapToDevicePixel(centerX - LABEL_WIDTH / 2);
    const top = snapToDevicePixel(labelBottom - getLabelHeight(label));
    label.style.transform = `translate(${left}px, ${top}px)`;
  };

  /**
   * Haengt die Ergaenzungen an die Lightbox eines media-gallery-Elements.
   * @param {HTMLElement} gallery Element media-gallery
   */
  const enhance = (gallery) => {
    const lightbox = gallery.lightbox;
    if (!lightbox || !lightbox.options || lightbox.kwEnhanced) return;
    lightbox.kwEnhanced = true;

    injectStyles();
    bottomPadding = getBottomPadding();

    // Bildflaeche endet ueber der Bedienleiste
    lightbox.options.paddingFn = () => ({ top: 0, bottom: bottomPadding, left: 0, right: 0 });

    // Touch: einfacher Tipp blendet die Bedienelemente nicht mehr aus (Standard waere 'toggle-controls')
    lightbox.options.tapAction = false;

    // Platzhalter je Slide: laeuft nach dem Filter des Themes (Prioritaet 100) und ersetzt dessen Wert
    lightbox.addFilter('placeholderSrc', (placeholderSrc, content) => {
      try {
        return findPlaceholderSrc(gallery, content);
      } catch (error) {
        console.error('kw-lightbox:', error);
        return placeholderSrc;
      }
    }, 200);

    /** Slides, die gerade ein Label tragen */
    const labeledSlides = new Set();

    lightbox.on('afterSetContent', safe(({ slide }) => {
      if (!slide || !slide.holderElement) return;
      if (!slide.kwAiLabel) {
        const label = createLabel(gallery, slide);
        if (!label) return;
        slide.kwAiLabel = label;
        slide.holderElement.appendChild(label);
        labeledSlides.add(slide);

        // Erst mit dem geladenen Bild steht die Hoehe des Labels genau fest
        const image = label.firstElementChild;
        if (image && !image.complete) {
          image.addEventListener('load', safe(() => positionLabel(slide)), { once: true });
        }
      }
      positionLabel(slide);
    }));

    // Zoomen, Verschieben und Ziehen der aktuellen Slide
    lightbox.on('zoomPanUpdate', safe(({ slide }) => {
      if (slide) positionLabel(slide);
    }));

    // Fenstergroesse geaendert: alle Slides mit Label neu ausrichten
    lightbox.on('resize', safe(() => {
      for (const slide of labeledSlides) positionLabel(slide);
    }));

    // PhotoSwipe verwendet die Slide-Halter wieder: Label mit der Slide entfernen
    lightbox.on('slideDestroy', safe(({ slide }) => {
      if (!slide || !slide.kwAiLabel) return;
      slide.kwAiLabel.remove();
      slide.kwAiLabel = null;
      labeledSlides.delete(slide);
    }));

    // Label erst nach der Oeffnen-Animation zeigen und beim Schliessen ausblenden
    lightbox.on('openingAnimationEnd', safe(() => {
      lightbox.pswp?.element?.classList.add(READY_CLASS);
    }));
    lightbox.on('closingAnimationStart', safe(() => {
      lightbox.pswp?.element?.classList.remove(READY_CLASS);
    }));

    lightbox.on('destroy', safe(() => {
      for (const slide of labeledSlides) {
        slide.kwAiLabel?.remove();
        slide.kwAiLabel = null;
      }
      labeledSlides.clear();
    }));
  };

  /** Klinkt enhance in initImageZoom von media-gallery ein (gilt fuer alle, auch spaeter erzeugte Galerien) */
  const patchMediaGallery = () => {
    const MediaGallery = customElements.get('media-gallery');
    if (!MediaGallery) return;

    const prototype = MediaGallery.prototype;
    const original = prototype.initImageZoom;
    if (typeof original !== 'function' || original.kwPatched) return;

    const patched = function (...args) {
      const result = original.apply(this, args);
      safe(enhance)(this);
      return result;
    };
    patched.kwPatched = true;
    prototype.initImageZoom = patched;
  };

  const start = async () => {
    await customElements.whenDefined('media-gallery');
    safe(patchMediaGallery)();

    // Galerien, deren Lightbox schon vor dem Einklinken entstanden ist
    for (const gallery of document.querySelectorAll('media-gallery')) {
      safe(enhance)(gallery);
    }
  };

  start();
})();